import { useState, useEffect } from 'react';

interface AccountSettings {
  name: string;
  email: string;
  role: string;
  avatarUrl: string;
}

interface SecuritySettings {
  twoFactorEnabled: boolean;
  lastPasswordChange: string;
  sessionTimeout: number;
}

interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  soundAlerts: boolean;
  weeklyDigest: boolean;
}

interface PreferenceSettings {
  theme: string;
  language: string;
  autoSave: boolean;
  compactView: boolean;
}

interface Settings {
  account: AccountSettings;
  security: SecuritySettings;
  notifications: NotificationSettings;
  preferences: PreferenceSettings;
}

// Mock initial settings
const mockSettings: Settings = {
  account: {
    name: 'John Doe',
    email: 'john@example.com',
    role: 'Administrator',
    avatarUrl: '',
  },
  security: {
    twoFactorEnabled: false,
    lastPasswordChange: new Date().toISOString(),
    sessionTimeout: 30,
  },
  notifications: {
    emailNotifications: true,
    pushNotifications: true,
    soundAlerts: false,
    weeklyDigest: true,
  },
  preferences: {
    theme: 'dark',
    language: 'en',
    autoSave: true,
    compactView: false,
  },
};

export const useSettings = () => {
  const [settings, setSettings] = useState<Settings>(mockSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Simulate API call
    const fetchSettings = async () => {
      try {
        setIsLoading(true);
        // Simulate network delay
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setSettings(mockSettings);
        setError(null);
      } catch (err) {
        setError('Failed to load settings');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const updateSettings = async (type: string, data: any) => {
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500));
      setSettings((prev) => ({
        ...prev,
        [type]: data,
      }));
      return true;
    } catch (err) {
      setError('Failed to update settings');
      return false;
    }
  };

  return { settings, updateSettings, isLoading, error };
};

export type { Settings, AccountSettings, SecuritySettings, NotificationSettings, PreferenceSettings }; 